// The welcome card (player-reach.md §8.5, R1572): card 10 of mockup round
// 075. It shows once, on the first join of the account's first Core season.
//
// The server decides when: `Empire.welcome` holds while the ticket's count
// of Core seasons is 1 and the player has not closed the card, and the
// EmpireSetting key `welcome_seen` closes it for good, so a second device
// does not show it again. The card replaces the handover notice on that
// join, because its second sentence is that notice.
//
// The card leads with the situation of the empire (R2120, the owner's
// ruling "Both, inventory folded"): the founding truce as a protection,
// the nearest hostile hold with the run of the fastest fleet to it, and
// the nearest other holds. Every fact comes off the hello and the Empire
// frame the client holds; nothing new rides the wire. A walk-in joiner
// takes over a seat the autopilot played since the season roll (R736), so
// the handover inventory (what the autopilot built) stands behind one
// press, folded. Numbered tags point at the advisor line (1) and at
// SUPPORT behind ☸ OPTIONS (2). On a telephone a third tag names VIEW ·
// FACTION KEY (3), because a telephone keeps the faction key off the glass
// (R1805, round 086 open point 3), and this card is the one line a new
// commander reads once.
//
// THE SEASON block stands under the situation (R2447): the goal of the
// season, who the commander plays with and what stays, the words of the
// conquest page of the HOW IT PLAYS card (R2445), and the press HOW IT
// PLAYS that opens that card. A seat with no clan of a person's own gets
// the press FIND A CLAN. On a telephone the head of the block is a fold
// press and the block starts folded, so the card stays short and its two
// buttons stay in the thumb zone (R2120, §8.5).
//
// This module is pure: the overlay gathers the facts and owns the element.

import { MAX_CLAN_MEMBERS, SEASON_DAYS } from '../gen/balance';
import { SiteKind, type Site } from '../gen/proto/aquaphobia';
import { WELCOME_NEIGHBOURS } from '../gen/presentation';
import { welcome as S } from '../gen/strings';
import { cellCenterX, cellCenterY } from '../world';
import { esc } from './esc';

/** The EmpireSetting key that closes the card (hub/trials.go). */
export const WELCOME_KEY = 'welcome_seen';

/** One line of a list: what it is, and the note at its right. */
export interface WelcomeItem {
  name: string;
  note: string;
}

/** The eight bearings, clockwise from north. */
export type Bearing = 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w' | 'nw';
const BEARINGS: readonly Bearing[] = ['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'];

/**
 * The bearing of a point from another, in world px: north is the top of
 * the map, so a smaller y is north. Each bearing covers 45° about its axis.
 */
export function bearingOf(dx: number, dy: number): Bearing {
  const deg = (Math.atan2(dx, -dy) * 180) / Math.PI;
  return BEARINGS[(Math.round(deg / 45) + 8) % 8];
}

/** The word of a bearing, from the sentence table. */
function bearingWord(b: Bearing): string {
  return S[`dir_${b}`];
}

/** One hold the card can name: its place in world px and its faction. */
export interface HoldPoint {
  name: string;
  faction: number;
  x: number;
  y: number;
}

/** A hold the card names, with its bearing and distance from the home hold. */
export interface NearHold {
  name: string;
  faction: number;
  bearing: Bearing;
  /** World px, in a straight line. */
  dist: number;
}

/**
 * The nearest hold of another faction (the threat), and the `count`
 * nearest other holds after it (the neighbours), nearest first. A hold of
 * faction 0 belongs to no side and takes no place. A tie in distance
 * falls to the name, so two clients name the same holds.
 */
export function nearHolds(home: { x: number; y: number }, faction: number, holds: readonly HoldPoint[],
  count: number): { threat: NearHold | null; neighbours: NearHold[] } {
  const near = holds.filter((h) => h.faction !== 0)
    .map((h): NearHold => {
      const dx = h.x - home.x, dy = h.y - home.y;
      return { name: h.name, faction: h.faction, bearing: bearingOf(dx, dy), dist: Math.hypot(dx, dy) };
    })
    .sort((a, b) => a.dist - b.dist || (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
  const threat = near.find((h) => h.faction !== faction) ?? null;
  return { threat, neighbours: near.filter((h) => h !== threat).slice(0, count) };
}

/** What `situationOf` reads: the client's own frames, and two clocks. */
export interface SituationInput {
  /** Every site of the hello; the card reads the player holds. */
  sites: Iterable<Pick<Site, 'kind' | 'cell' | 'faction' | 'name' | 'ownerPlayer'>>;
  playerId: number;
  faction: number;
  /** The seat's fleets, at sea and in the pens, with the class speed. */
  fleets: { name: string; speedUh: number }[];
  /** Map seconds to world seconds (`GameState.mapToWorldSec`). */
  toWorldSec: (mapSec: number) => number;
  /** Writes a duration (`state.ts` `dur`). */
  dur: (sec: number) => string;
  factionName: (id: number) => string;
}

/**
 * The threat and the neighbours of the card (R2120), off the holds of the
 * hello. The run is the straight line at the class speed of the fastest
 * fleet: a speed is world px an hour on the map clock (numbers.md §3), and
 * the card shows it on the world clock, as every duration of the client.
 * The rock on the way makes the true run longer, so the sentence says
 * "about".
 */
export function situationOf(i: SituationInput): Pick<WelcomeFacts, 'threat' | 'neighbours'> {
  let home: { x: number; y: number } | null = null;
  const holds: HoldPoint[] = [];
  for (const site of i.sites) {
    if (site.kind !== SiteKind.SITE_KIND_PLAYER_BASE) continue;
    const at = { x: cellCenterX(site.cell), y: cellCenterY(site.cell) };
    if (site.ownerPlayer === i.playerId) home = at;
    else holds.push({ name: site.name, faction: site.faction, ...at });
  }
  if (!home) return { threat: null, neighbours: [] };
  const near = nearHolds(home, i.faction, holds, WELCOME_NEIGHBOURS);
  let threat: WelcomeFacts['threat'] = null;
  if (near.threat) {
    const t = near.threat;
    let fast: { name: string; speedUh: number } | null = null;
    for (const f of i.fleets) if (f.name !== '' && f.speedUh > 0 && (!fast || f.speedUh > fast.speedUh)) fast = f;
    const run = fast && t.dist > 0
      ? { fleet: fast.name, time: i.dur(i.toWorldSec((t.dist / fast.speedUh) * 3600)) }
      : null;
    threat = { hold: t.name, faction: i.factionName(t.faction), bearing: t.bearing, run };
  }
  return {
    threat,
    neighbours: near.neighbours.map((n) => ({
      hold: n.name, faction: n.faction === i.faction ? '' : i.factionName(n.faction), bearing: n.bearing,
    })),
  };
}

/** A written duration as one unit: "9h 4m" never breaks between its
 *  two parts at the end of a line (R2138). */
export function keepFigure(written: string): string {
  return written.replace(/ /g, '\u00a0');
}

/** The truce sentence: the figure while the truce runs, else its end. */
export function truceLine(leftSec: number, written: string): string {
  return leftSec > 0 ? S.truce({ left: keepFigure(written) }) : S.truce_over;
}

/** Everything the card states, gathered once when it opens. */
export interface WelcomeFacts {
  season: number;
  day: number;
  faction: string;
  hold: string;
  /** The squad and its clan, or null when the seat flies in no squad. */
  squad: { squad: number; clan: string } | null;
  /** The seat flies in no clan of a person's own: a pickup squad of a
   *  default clan, or no clan. THE SEASON block then offers FIND A CLAN. */
  clanless: boolean;
  /** The founding truce left in world seconds, and that figure written.
   *  The overlay writes the sentence again while the card stands. */
  truceSec: number;
  truce: string;
  /** The nearest hostile hold, or null when the card cannot tell. The
   *  faction is already its name, and `run` names the fastest fleet of
   *  the seat with its run there, or is null for a seat with no fleet. */
  threat: { hold: string; faction: string; bearing: Bearing; run: { fleet: string; time: string } | null } | null;
  /** The nearest other holds after the threat. `faction` is '' for a hold
   *  of the player's own faction. */
  neighbours: { hold: string; faction: string; bearing: Bearing }[];
  buildings: WelcomeItem[];
  research: WelcomeItem[];
  fleets: WelcomeItem[];
  captains: WelcomeItem[];
  /** The seat keeps the sea trials, so the SEA TRIALS button stands. */
  trials: boolean;
  /** The sheet form factor: the card names VIEW · FACTION KEY (R1805). */
  sheet: boolean;
}

/** The note of a building: its level. */
export function levelNote(level: number): string {
  return S.level({ level });
}

/** The note of a research node: done, or waiting in the queue. */
export function researchNote(done: boolean): string {
  return done ? S.research_done : S.research_queued;
}

/** The note of a fleet: its hull class, and the pens for a penned one. */
export function fleetNote(cls: string, penned: boolean): string {
  return penned ? S.fleet_pens({ cls }) : cls;
}

/** The note of a captain who commands no fleet. */
export const ASHORE = S.captain_ashore;

/** The note of a neighbour: its faction, or "your faction", and its bearing. */
export function neighbourNote(faction: string, b: Bearing): string {
  const dir = bearingWord(b);
  return faction === '' ? S.neighbour_own({ dir }) : S.neighbour({ faction, dir });
}

/** The words of the fold press: the head, then each count that is not 0. */
export function builtLabel(f: Pick<WelcomeFacts, 'buildings' | 'fleets' | 'captains'>): string {
  const parts = [S.built];
  if (f.buildings.length > 0) parts.push(S.built_buildings({ n: f.buildings.length }));
  if (f.fleets.length > 0) parts.push(S.built_fleets({ n: f.fleets.length }));
  if (f.captains.length > 0) parts.push(S.built_captains({ n: f.captains.length }));
  return parts.join(' · ');
}

/** The id of the folded inventory, which the fold press controls. */
export const BUILT_ID = 'welcome-built';

/** The id of THE SEASON block, which its fold press controls on a
 *  telephone (R2447). */
export const SEASON_ID = 'welcome-season';

/** One headed list, or '' for an empty one. */
function list(head: string, items: WelcomeItem[]): string {
  if (items.length === 0) return '';
  return `<div class="st">${head}</div><ul>`
    + items.map((i) => `<li><span>${esc(i.name)}</span><span class="q">${esc(i.note)}</span></li>`).join('')
    + '</ul>';
}

/** The situation: the truce, the nearest hostile hold and the neighbours. */
function situation(f: WelcomeFacts): string {
  let out = `<p class="truce">${esc(truceLine(f.truceSec, f.truce))}</p>`;
  if (f.threat) {
    const t = f.threat;
    out += `<p>${esc(S.threat({ hold: t.hold, faction: t.faction, dir: bearingWord(t.bearing) }))}`
      + (t.run ? ` ${esc(S.threat_time({ fleet: t.run.fleet, time: keepFigure(t.run.time) }))}` : '')
      + '</p>';
  }
  return out + list(S.head_neighbours,
    f.neighbours.map((n) => ({ name: n.hold, note: neighbourNote(n.faction, n.bearing) })));
}

/**
 * THE SEASON block (R2447): the goal, the team, the clan line for a seat
 * with no clan of its own, what stays, and the press HOW IT PLAYS. On a
 * telephone (`f.sheet`) the head is a fold press and the block starts
 * folded; a desktop shows the block open under a plain head.
 */
function season(f: WelcomeFacts): string {
  const clan = f.clanless
    ? `<p>${esc(S.season_clan({ members: MAX_CLAN_MEMBERS }))}</p>` : '';
  const presses = '<div class="presses">'
    + `<button type="button" data-act="welcomehow">${esc(S.how)}</button>`
    + (f.clanless
      ? `<button type="button" data-act="welcomeclan" title="${esc(S.find_clan_title)}">${esc(S.find_clan)}</button>`
      : '')
    + '</div>';
  const body = `<p>${esc(S.season_goal({ days: SEASON_DAYS }))}</p>`
    + `<p>${esc(S.season_team)}</p>${clan}`
    + `<p class="dim">${esc(S.season_stays)}</p>${presses}`;
  if (!f.sheet) return `<div class="season"><div class="st">${esc(S.head_season)}</div>${body}</div>`;
  return '<div class="season">'
    + `<button type="button" class="fold" data-act="welcomeseason" aria-expanded="false" aria-controls="${SEASON_ID}">`
    + `${esc(S.head_season)}</button><div id="${SEASON_ID}" hidden>${body}</div></div>`;
}

/** The numbered tag of a place the card points at. */
export function calloutTag(n: number): string {
  return `<span class="ctag">${n}</span>`;
}

/**
 * The card's markup. The head states what the player commands, then the
 * situation, then the handover inventory folded behind one press (R2120).
 * The body scrolls inside the card, and the two buttons stand under it, in
 * the thumb zone on a phone. TAKE COMMAND closes the card; SEA TRIALS
 * closes it and opens the trials. The fold press opens the inventory in
 * place through `toggleBuilt`.
 */
export function welcomeHtml(f: WelcomeFacts): string {
  const built = list(S.head_buildings, f.buildings) + list(S.head_research, f.research);
  const crew = list(S.head_fleets, f.fleets) + list(S.head_captains, f.captains);
  const fold = built + crew === '' ? ''
    : `<button type="button" class="fold" data-act="welcomefold" aria-expanded="false" aria-controls="${BUILT_ID}">`
      + `${esc(builtLabel(f))}</button>`
      + `<div class="lists" id="${BUILT_ID}" hidden><div>${built}</div><div>${crew}</div></div>`;
  return '<div class="body">'
    + `<div class="k">${esc(S.kicker({ season: f.season, day: f.day, faction: f.faction.toUpperCase() }))}</div>`
    + `<h4>${S.heading}</h4>`
    + `<p>${esc(S.command({ hold: f.hold }))}`
    + (f.squad ? ` ${esc(S.squad({ squad: f.squad.squad, clan: f.squad.clan }))}` : '')
    + '</p>'
    + `<p class="dim">${esc(S.handover)}</p>`
    + `<div class="sit">${situation(f)}</div>`
    + season(f)
    + fold
    + `<div class="where"><span>${calloutTag(1)}${esc(S.where_advisor)}</span>`
    + `<span>${calloutTag(2)}${esc(S.where_support)}</span>`
    + (f.sheet ? `<span>${calloutTag(3)}${esc(S.where_key)}</span>` : '')
    + '</div></div>'
    + '<div class="foot">'
    + (f.trials ? `<button type="button" data-act="welcometrials">${S.trials}</button>` : '')
    + `<button type="button" class="primary" data-act="welcomego">${S.go}</button></div>`;
}

/** Opens or folds the handover inventory in place, and says which. */
export function toggleBuilt(card: HTMLElement): boolean {
  return toggleFold(card, BUILT_ID);
}

/** Opens or folds THE SEASON block in place on a telephone, and says
 *  which (R2447). */
export function toggleSeason(card: HTMLElement): boolean {
  return toggleFold(card, SEASON_ID);
}

/** Opens or folds the region `id` under its fold press. */
function toggleFold(card: HTMLElement, id: string): boolean {
  const btn = card.querySelector<HTMLButtonElement>(`button.fold[aria-controls="${id}"]`);
  const lists = card.querySelector<HTMLElement>(`#${id}`);
  if (!btn || !lists) return false;
  const open = btn.getAttribute('aria-expanded') !== 'true';
  btn.setAttribute('aria-expanded', String(open));
  lists.hidden = !open;
  return open;
}
